import React, { FC, useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useTenantCode } from 'utils/hooks/useTenantConfig';
import { useDataTableColumnConfigurations } from 'utils/hooks/useDataTableColumnConfigurations/useDataTableColumnConfigurations';
import { RateCardModel } from '@utmost.co/enterprise-models';
import { DataTable, DataTablePaginator } from '@utmost.co/utmost-design-system';
import { useFrontdoorRoute } from 'src/components/pages/frontdoor/config/useFrontdoorRoute';
import { ENGAGEMENTS_TAB, PATH_SCHEMA } from 'src/components/pages/settings/Engagements/EngagementsHome.constants';
import { DeleteRateCardDialog } from 'src/components/pages/settings/Engagements/RateCards/DeleteRateCardDialog';
import RateCardDrawer from 'src/components/pages/settings/Engagements/RateCards/drawer/RateCardDrawer';
import { RateCardsToolbar } from 'src/components/pages/settings/Engagements/RateCards/RateCardsToolbar';
import { useRateCardColumns } from './rateCardColumns';
import { useGetRateCardData } from './useGetRateCardData';
import { useRateCardData } from 'src/components/pages/frontdoor/forms/AddRateCard/useRateCardData';

export const RateCardsPage: FC<React.PropsWithChildren<unknown>> = () => {
  const { rateCards, totalCount, loading, variables, setVariables, refetch } = useGetRateCardData();
  const { addRateCard } = useFrontdoorRoute();
  const { push } = useHistory();

  const selectedRateCard = useHistory().location.state as RateCardModel;

  const { remove } = useRateCardData();

  const { columnConfiguration, patchColumnConfiguration } = useDataTableColumnConfigurations('RATE_CARDS', {
    hiddenColumns: [
      'overTimeRateInfo.billRate',
      'overTimeRateInfo.payRate',
      'overTimeRateInfo.markup',
      'doubleTimeRateInfo.billRate',
      'doubleTimeRateInfo.payRate',
      'doubleTimeRateInfo.markup',
      'shiftRateInfo.billRate',
      'shiftRateInfo.payRate',
      'shiftRateInfo.factor'
    ]
  });

  const onRateCardAdd = useCallback(() => {
    addRateCard.go();
  }, [addRateCard]);

  const [deleteUid, setDeleteUid] = useState<string | undefined>();
  const onDeleteCancel = useCallback(() => setDeleteUid(undefined), []);

  const onDeleteConfirm = useCallback(async () => {
    if (deleteUid) {
      await remove(deleteUid);
      setDeleteUid(undefined);
      refetch();
    }
  }, [deleteUid, remove, refetch]);

  const onRateCardRemove = useCallback((uid: string) => setDeleteUid(uid), []);

  const columns = useRateCardColumns({ onDelete: onRateCardRemove });

  const tenantCode = useTenantCode();
  const location = useLocation();

  const isDrawerOpen = useMemo(() => {
    const drawerPath = `/${tenantCode}${PATH_SCHEMA}/${ENGAGEMENTS_TAB.RATE_CARDS}/details/`;
    return location.pathname.includes(drawerPath);
  }, [location, tenantCode]);

  const handleCloseDrawer = useCallback(() => {
    const destination = `/${tenantCode}${PATH_SCHEMA}/${ENGAGEMENTS_TAB.RATE_CARDS}`;
    push({ pathname: destination });
  }, [push, tenantCode]);

  return (
    <>
      <DataTable
        isFetching={loading}
        columns={columns}
        totalCount={totalCount}
        data={rateCards}
        onColumnConfigurationChanged={patchColumnConfiguration}
        initialState={columnConfiguration}
        onVariablesChanged={setVariables}
        querySettings={{ useParams: true, manualSortBy: true }}
      >
        <RateCardsToolbar
          searchString={variables?.query?.search}
          onRateCardAdd={onRateCardAdd}
          message={`Viewing ${rateCards?.length} of ${totalCount} Rate Card${totalCount === 1 ? '' : 's'}`}
        />

        <DataTablePaginator />
      </DataTable>
      <RateCardDrawer rateCard={selectedRateCard} show={isDrawerOpen} onClose={handleCloseDrawer} />
      <DeleteRateCardDialog open={!!deleteUid} onConfirm={onDeleteConfirm} onCancel={onDeleteCancel} />
    </>
  );
};
